import { useAnnotateWriteOffDiscrepancyMutation, type WriteOffDiscrepancy } from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { decisionLabel, fmtMoney, fmtNum } from '@shared/lib/smetaLabels';
import { discrTone, fmtDelta, kindLabel, severityLabel } from '../helpers';
import { DecisionBtns, Muted, Table } from '../styles';
import { DecisionSaveCaption } from '../../DecisionSaveError';
import { useDecisionSave } from '../../decisionSave';

export function DiscrepanciesTable({
  items,
  checkId,
}: {
  items: WriteOffDiscrepancy[];
  checkId: string;
}) {
  const [annotate, { isLoading }] = useAnnotateWriteOffDiscrepancyMutation();
  const { error: saveError, save } = useDecisionSave();
  const { can_write } = useAgentPermission('smeta');
  if (items.length === 0) {
    return <Muted $mt={8}>Расхождений не найдено.</Muted>;
  }
  return (
    <Table>
      <DecisionSaveCaption message={saveError} />
      <thead>
        <tr>
          <th>Важность</th>
          <th>Тип</th>
          <th>Материал</th>
          <th>Ед.</th>
          <th className="num">Норма</th>
          <th className="num">Факт</th>
          <th className="num">Δ</th>
          <th className="num">Стоимость Δ</th>
          <th>Решение</th>
        </tr>
      </thead>
      <tbody>
        {items.map((d) => (
          <tr key={d.id}>
            <td>
              <Badge $tone={discrTone(d.severity)}>{severityLabel(d.severity)}</Badge>
            </td>
            <td>{kindLabel(d.kind)}</td>
            <td>
              {d.material_name}
              <Muted>{d.description}</Muted>
            </td>
            <td>{d.unit ?? '—'}</td>
            <td className="num">{fmtNum(d.norm_quantity)}</td>
            <td className="num">{fmtNum(d.fact_quantity)}</td>
            <td className="num">{fmtDelta(d)}</td>
            <td className="num">{fmtMoney(d.delta_cost)}</td>
            <td>
              {d.decision ? (
                <Badge
                  $tone={
                    d.decision === 'accepted'
                      ? 'success'
                      : d.decision === 'rejected'
                        ? 'neutral'
                        : 'warning'
                  }
                >
                  {decisionLabel(d.decision)}
                </Badge>
              ) : (
                <DecisionBtns>
                  <Button
                    $variant="ghost"
                    $size="sm"
                    type="button"
                    disabled={isLoading || !can_write}
                    title={!can_write ? 'Только чтение' : undefined}
                    onClick={() => void save(() => annotate({ id: d.id, checkId, decision: 'accepted' }))}
                  >
                    Принять
                  </Button>
                  <Button
                    $variant="ghost"
                    $size="sm"
                    type="button"
                    disabled={isLoading || !can_write}
                    title={!can_write ? 'Только чтение' : undefined}
                    onClick={() => void save(() => annotate({ id: d.id, checkId, decision: 'disputed' }))}
                  >
                    Спорно
                  </Button>
                  <Button
                    $variant="ghost"
                    $size="sm"
                    type="button"
                    disabled={isLoading || !can_write}
                    title={!can_write ? 'Только чтение' : undefined}
                    onClick={() => void save(() => annotate({ id: d.id, checkId, decision: 'rejected' }))}
                  >
                    Отклонить
                  </Button>
                </DecisionBtns>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
